import { useCallback, useEffect, useMemo, useState } from 'react';

// eslint-disable-next-line import/no-extraneous-dependencies -- Platform-provided dependency
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { TIME_CONSTANTS } from '../../../constants/time';
import { calculateBannerVisibility } from '../../../services/heartbeat/visibilityLogic';
import { getIdentityLogoutUrl } from '../../../utils/identity';
import { type createStorageAPI, STORAGE_KEYS } from '../../../utils/typed-storage';
import { canDismissBanner } from '../utils/bannerUtils';
import { getSessionUrgencyLevel } from '../utils/urgencyLevel';

export interface BannerStateReturn {
	/** Current time remaining in milliseconds */
	timeRemainingMs: number;
	/** Whether banner is currently visible */
	isVisible: boolean;
	/** Whether banner can be dismissed (not in final 15 minutes) */
	canHide: boolean;
	/** Handler to dismiss the banner */
	handleDismiss: () => void;
	/** Handler for re-authentication click */
	handleReauthClick: (event: React.MouseEvent) => void;
	/** Whether we've received initial heartbeat response */
	hasReceivedHeartbeat: boolean;
}

/**
 * Custom hook that manages all banner state including visibility, dismissal, and user interactions.
 * Centralizes the complex banner state logic for better organization and testability.
 */
export const useBannerState = (
	sessionExpiryTimestamp: number | null,
	lastSessionExpiry: number | null = null,
	storageAPI: ReturnType<typeof createStorageAPI>,
): BannerStateReturn => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	// Prevent banner flash: don't show until first heartbeat call completes
	const [hasReceivedHeartbeat, setHasReceivedHeartbeat] = useState(false);

	// Calculate effective session expiry using unified logic
	const effectiveSessionExpiry = useMemo((): number | null => {
		// Don't show banner until first heartbeat completes (prevents all flash scenarios)
		if (!hasReceivedHeartbeat) {
			return null;
		}

		// Cross-tab sync already validates sessions - just use it if available
		if (lastSessionExpiry) {
			return lastSessionExpiry;
		}

		// Otherwise use this tab's session expiry
		return sessionExpiryTimestamp;
	}, [sessionExpiryTimestamp, lastSessionExpiry, hasReceivedHeartbeat]);

	// State management
	const [timeRemainingMs, setTimeRemainingMs] = useState<number>(0);
	const [isVisible, setIsVisible] = useState(false);

	/**
	 * Calculate visibility state using extracted visibility logic.
	 */
	const calculateVisibility = useCallback((): boolean => {
		// Don't show banner until first heartbeat arrives
		if (!hasReceivedHeartbeat) {
			return false;
		}
		return calculateBannerVisibility(effectiveSessionExpiry, null, null, storageAPI);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [effectiveSessionExpiry, hasReceivedHeartbeat]);

	/**
	 * Determine if banner can be hidden based on time remaining.
	 * Cannot be used if less than 15 minutes remaining.
	 */
	const canHide = timeRemainingMs > TIME_CONSTANTS.FIFTEEN_MINUTES_MS;

	/**
	 * Handle banner dismissal with "Remind me later".
	 * Saves dismissal state and hides banner until next milestone.
	 */
	const handleDismiss = useCallback(async (): Promise<void> => {
		if (!effectiveSessionExpiry) {
			return;
		}

		const timeUntilExpiry = effectiveSessionExpiry - Date.now();

		// Don't allow hiding if less than 15 minutes remaining
		if (!canDismissBanner(timeUntilExpiry)) {
			return;
		}

		// Calculate next milestone for hideUntil time
		const now = Date.now();
		const milestones = [
			TIME_CONSTANTS.TWO_HOURS_MS,
			TIME_CONSTANTS.ONE_HOUR_MS,
			TIME_CONSTANTS.THIRTY_MINUTES_MS,
			TIME_CONSTANTS.FIFTEEN_MINUTES_MS,
		];

		// Find the next milestone to hide until
		// Skip any milestones that are at or before the current dismissal point
		let hideUntilTime = effectiveSessionExpiry - TIME_CONSTANTS.FIFTEEN_MINUTES_MS; // Default: 15-minute mark
		let foundCurrentMilestone = false;
		let foundNextMilestone = false;

		for (const milestone of milestones) {
			const milestoneTime = effectiveSessionExpiry - milestone;
			// Check if we're close to this milestone (within grace period before it)
			// Only check the grace period BEFORE the milestone, not after
			const gracePeriodStart = milestoneTime - TIME_CONSTANTS.MILESTONE_GRACE_PERIOD_MS;

			const isAtThisMilestone = now >= gracePeriodStart && now <= milestoneTime;

			if (isAtThisMilestone) {
				// We're at or approaching this milestone, skip it and use the next one
				foundCurrentMilestone = true;
				continue;
			}

			// If we've found the current milestone, this is the next one
			if (foundCurrentMilestone) {
				hideUntilTime = milestoneTime;
				foundNextMilestone = true;
				break;
			}

			// If we haven't found current milestone yet, select the first future milestone
			if (!foundNextMilestone && milestoneTime > now) {
				hideUntilTime = milestoneTime;
				foundNextMilestone = true;
				// Don't break - we might find we're at a milestone further down the list
			}
		}

		const dismissalData = {
			hideUntil: hideUntilTime,
		};

		// Write to storage - may return Promise (axLocalStorage) or StorageResult (localStorage)
		const setResult = storageAPI.sessionBannerDismissed.set(dismissalData);

		// If async storage, await completion before hiding banner
		if (setResult instanceof Promise) {
			await setResult.catch(() => {
				// Silent failure - storage errors should not interfere with application
			});
		}

		// Hide banner after storage write completes
		setIsVisible(false);
	}, [effectiveSessionExpiry, storageAPI]);

	/**
	 * Handle re-authentication click.
	 * Prevents default navigation and redirects to identity logout with full URI in continue param.
	 */
	const handleReauthClick = useCallback((event: React.MouseEvent) => {
		event.preventDefault();
		// Use getIdentityLogoutUrl to construct proper logout URL with full URI in continue parameter
		window.location.href = getIdentityLogoutUrl();
	}, []);

	// ALWAYS wait for first heartbeat call to complete - no exceptions!
	// This prevents flash in ALL scenarios (cross-tab, re-auth, normal startup)
	useEffect(() => {
		if (
			sessionExpiryTimestamp !== null &&
			sessionExpiryTimestamp !== undefined &&
			!hasReceivedHeartbeat
		) {
			setHasReceivedHeartbeat(true);
		}
	}, [sessionExpiryTimestamp, hasReceivedHeartbeat]);

	/**
	 * Immediate effect: updates when effectiveSessionExpiry changes (reactive programming).
	 * This ensures cross-tab sync, re-auth detection, and new API data trigger immediate updates.
	 */
	useEffect(() => {
		if (!effectiveSessionExpiry) {
			setIsVisible(false);
			setTimeRemainingMs(0);
			return;
		}

		const timeRemaining = effectiveSessionExpiry - Date.now();
		setTimeRemainingMs(timeRemaining);
		const shouldShow = calculateVisibility();

		// Track when banner becomes visible
		// Note: isVisible is not in dependencies to avoid re-render loops
		if (shouldShow && !isVisible) {
			createAnalyticsEvent({
				action: 'viewed',
				actionSubject: 'banner',
				actionSubjectId: 'sessionWarning',
				eventType: 'operational',
				source: 'heartbeat',
				attributes: {
					timeRemainingMs: timeRemaining,
					canDismiss: timeRemaining > TIME_CONSTANTS.FIFTEEN_MINUTES_MS,
					urgencyLevel: getSessionUrgencyLevel(timeRemaining),
					sessionExpiryTimestamp: effectiveSessionExpiry,
				},
			}).fire('heartbeat');
		}

		setIsVisible(shouldShow);
		// eslint-disable-next-line react-hooks/exhaustive-deps -- isVisible intentionally omitted to avoid re-render loops
	}, [effectiveSessionExpiry, calculateVisibility, createAnalyticsEvent]);

	/**
	 * Timer effect: updates time remaining display for countdown progression.
	 * Updates every 10 seconds as real time passes (needed for live countdown).
	 */
	useEffect(() => {
		if (!effectiveSessionExpiry) {
			return;
		}

		// Update every 10 seconds for countdown progression
		const interval = setInterval(() => {
			const timeRemaining = effectiveSessionExpiry - Date.now();
			setTimeRemainingMs(timeRemaining);
			const shouldShow = calculateVisibility();
			setIsVisible(shouldShow);
		}, TIME_CONSTANTS.UPDATE_INTERVAL_MS);

		return () => clearInterval(interval);
	}, [effectiveSessionExpiry, calculateVisibility]);

	/**
	 * Clear dismissal when cross-tab sync detects a significantly longer session.
	 * This ensures users see good news when another tab extends their session.
	 */
	useEffect(() => {
		if (sessionExpiryTimestamp && lastSessionExpiry) {
			const difference = lastSessionExpiry - sessionExpiryTimestamp;

			// If cross-tab session is significantly longer (30+ minutes), clear dismissal
			if (difference >= TIME_CONSTANTS.THIRTY_MINUTES_MS) {
				storageAPI.sessionBannerDismissed.remove();

				// Immediately recalculate visibility
				if (effectiveSessionExpiry) {
					const timeRemaining = effectiveSessionExpiry - Date.now();
					setTimeRemainingMs(timeRemaining);
					const shouldShow = calculateVisibility();
					setIsVisible(shouldShow);
				}
			}
		}
	}, [
		sessionExpiryTimestamp,
		lastSessionExpiry,
		effectiveSessionExpiry,
		calculateVisibility,
		storageAPI,
	]);

	/**
	 * Listen for cross-tab dismissal changes via storage events.
	 * Session expiry changes are handled by useCrossTabSync hook updating lastSessionExpiry prop.
	 */
	useEffect(() => {
		const handleStorageChange = (event: StorageEvent): void => {
			if (event.key === STORAGE_KEYS.SESSION_BANNER_DISMISSED) {
				if (!effectiveSessionExpiry) {
					return;
				}

				// Let the main timer handle time calculations consistently
				// Just trigger visibility recalculation with current state
				const shouldShow = calculateVisibility();
				setIsVisible(shouldShow);
			}
		};

		window.addEventListener('storage', handleStorageChange);
		return () => {
			window.removeEventListener('storage', handleStorageChange);
		};
	}, [effectiveSessionExpiry, calculateVisibility]);

	return {
		timeRemainingMs,
		isVisible,
		canHide,
		handleDismiss,
		handleReauthClick,
		hasReceivedHeartbeat,
	};
};
